import { FunctionComponent, useState } from "react";

import { faChevronDown, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { Toggle } from "@/components/Toggle/Toggle";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import { EmailModeSwitch } from "./EmailModeSwitch";
import { LinkedinTierBadge } from "./LinkedinTierBadge";
import { EmailMode, LinkedinTier, SenderUserRow, UnmetRequirement } from "./senderSelector.types";

type SelectableMode = Exclude<EmailMode, "custom">;

const IneligibilityTip: FunctionComponent<{ unmet: UnmetRequirement[] }> = ({ unmet }) => (
	<div className="text-xs">
		<div className="mb-1 font-semibold text-gray-900">
			This sender can&apos;t be used for this campaign.
		</div>
		<ul className="flex list-disc flex-col gap-0.5 pl-4 text-text-meta">
			{unmet.map((requirement) => (
				<li key={requirement.channel}>
					Needs {requirement.label} — the campaign has {requirement.stepLabel}.
				</li>
			))}
		</ul>
	</div>
);

/**
 * One sending user in the senders table: a select checkbox, name, LinkedIn tier badge with its own
 * LinkedIn toggle (the tick means "some channel on", so LinkedIn needs a control of its own —
 * PREC-4324), the email mode switch (or a "connect emails" prompt when the user has none), and an expandable list of the
 * user's individual inboxes (primary + secondaries), each toggleable on its own.
 */
export const SenderRow: FunctionComponent<{
	row: SenderUserRow;
	tier: LinkedinTier;
	mode: EmailMode;
	selected: boolean;
	/** Whether the user's LinkedIn sender is on for this campaign (PREC-4324). */
	linkedinActive: boolean;
	eligible: boolean;
	unmet: UnmetRequirement[];
	error?: boolean;
	onToggleSelected: () => void;
	onToggleLinkedin: () => void;
	onModeChange: (mode: SelectableMode) => void;
	onToggleEmail: (uuid: string) => void;
	onConnectEmails: () => void;
}> = ({
	row,
	tier,
	mode,
	selected,
	linkedinActive,
	eligible,
	unmet,
	error,
	onToggleSelected,
	onToggleLinkedin,
	onModeChange,
	onToggleEmail,
	onConnectEmails,
}) => {
	const [expanded, setExpanded] = useState<boolean>(false);
	const hasEmail = row.emailSenders.length > 0;

	return (
		<div
			className={classNames(
				"border-b border-border-light last:border-b-0",
				!eligible && "opacity-60"
			)}
		>
			{/*
			 * `flex-wrap` so a narrow container drops the channel controls onto their own line instead
			 * of overflowing: the name is `whitespace-nowrap` and the mode switch has its own minimum,
			 * so without wrapping the row's min-content width simply exceeds the container and spills.
			 * Wide layout is unchanged — wrapping only engages once the content does not fit.
			 */}
			<div className="flex flex-wrap items-center gap-3 py-2.5">
				<div className="w-5 shrink-0">
					<Checkbox
						error={error}
						isChecked={selected}
						isDisabled={!eligible}
						showError={!!error}
						onChange={onToggleSelected}
					/>
				</div>
				<span className="whitespace-nowrap text-sm font-semibold text-gray-900">{row.name}</span>
				<LinkedinTierBadge active={linkedinActive} tier={tier} />
				{row.linkedinSender && (
					<Tooltip
						content={
							linkedinActive
								? "LinkedIn steps are sent from this account"
								: "LinkedIn is off: this user sends no LinkedIn steps"
						}
					>
						<Toggle
							disabled={!eligible}
							isChecked={linkedinActive}
							testId={`sender-linkedin-toggle-${row.userId}`}
							onChange={onToggleLinkedin}
						/>
					</Tooltip>
				)}

				{!eligible && unmet.length > 0 && (
					<Tooltip content={<IneligibilityTip unmet={unmet} />}>
						<span className="inline-flex cursor-help items-center gap-1 rounded-md bg-status-warning-light px-2 py-1 text-xs font-semibold text-text-warning">
							<Icon className="h-3 w-3" icon={faTriangleExclamation} />
							Unavailable
						</span>
					</Tooltip>
				)}

				<span className="flex-1" />

				{/*
				 * The trailing controls wrap as one unit, and `ml-8` indents them past the checkbox on
				 * the line they wrap onto (the mockup's own treatment). At a width where nothing wraps
				 * the margin is absorbed by the `flex-1` spacer above, so it is invisible there.
				 */}
				<div className="ml-8 flex items-center gap-3">
					{hasEmail ? (
						<EmailModeSwitch
							counts={{
								primary: row.primaryEmails.length,
								secondary: row.secondaryEmails.length,
								both: row.emailSenders.length,
							}}
							disableSecondary={row.secondaryEmails.length === 0}
							disabled={!eligible}
							mode={mode}
							size="sm"
							onChange={onModeChange}
						/>
					) : (
						<button
							className="inline-flex cursor-pointer items-center justify-center rounded-md border border-status-error-light bg-status-error-light/60 px-4 py-1.5 text-xs font-semibold text-status-error hover:bg-status-error-light"
							title="Connect emails"
							type="button"
							onClick={onConnectEmails}
						>
							No email connected
						</button>
					)}

					{hasEmail ? (
						<button
							aria-label="Show emails"
							className="grid h-6 w-6 cursor-pointer place-items-center rounded-md text-text-meta hover:bg-brand-white"
							type="button"
							onClick={() => setExpanded((value) => !value)}
						>
							<Icon
								className={classNames(
									"transition-transform duration-200",
									expanded && "rotate-180"
								)}
								icon={faChevronDown}
							/>
						</button>
					) : (
						<span className="w-6" />
					)}
				</div>
			</div>

			{hasEmail && expanded && (
				<div className="mb-2 rounded-lg bg-selected-item px-3 py-1.5">
					{[...row.primaryEmails, ...row.secondaryEmails].map((email) => (
						<div key={email.uuid} className="flex items-center gap-2 py-1.5">
							<div className="w-5 shrink-0">
								<Checkbox
									isChecked={email.active}
									isDisabled={!eligible}
									onChange={() => onToggleEmail(email.uuid)}
								/>
							</div>
							<span className="truncate text-sm text-text-meta">{email.emailAddress}</span>
							<span className="flex-1" />
							<span
								className={classNames(
									"rounded px-1.5 py-0.5 text-xxs font-bold uppercase tracking-wide",
									email.emailType === "primary"
										? "bg-blue-action-200 text-brand-light"
										: "bg-status-info-light text-text-meta"
								)}
							>
								{email.emailType === "primary" ? "Primary" : "Secondary"}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};
